import React, { useEffect, useMemo, useState } from "react";
import { reports as reportsApi, hotels } from "../api/endpoints";
import { DataTableShell, FilterToolbar, MetricStrip, PageHeader, SectionCard } from "../components/Workspace";
import { triggerDownload } from "../utils/download";
import type { ManagementCompany, ManagementDashboardOut, User } from "../types";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MIN_REPORT_YEAR = 2013;

interface Props {
  user: User;
}

export default function ManagementDashboardPage({ user }: Props) {
  const isGuest = user.role?.name === "Guest";
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState<number | "ytd">(new Date().getMonth() + 1);
  const [mcId, setMcId] = useState<number | undefined>(user.report_management_company_id || undefined);
  const [mcList, setMcList] = useState<ManagementCompany[]>([]);
  const [managementDashboard, setManagementDashboard] = useState<ManagementDashboardOut | null>(null);

  const params = useMemo(
    () => `year=${year}${month === "ytd" ? "" : `&month=${month}`}${mcId ? `&mc_id=${mcId}` : ""}`,
    [year, month, mcId]
  );
  const selectedMonthName = month === "ytd" ? "" : MONTH_NAMES[month - 1];
  const lastTrendRow = managementDashboard?.volume_by_month[managementDashboard.volume_by_month.length - 1];
  const trendEndMonth = lastTrendRow?.month_name || selectedMonthName || "Dec";
  const periodLabel = month === "ytd" ? `YTD ${year}` : `${selectedMonthName} ${year}`;
  const trendLabel = month === "ytd" ? `${year} YTD` : `${year} through ${selectedMonthName}`;
  const trendRangeLabel = `Jan to ${trendEndMonth} ${year}`;
  const availableYears = useMemo(
    () => Array.from({ length: currentYear - MIN_REPORT_YEAR + 2 }, (_, index) => MIN_REPORT_YEAR + index).reverse(),
    [currentYear]
  );

  useEffect(() => {
    hotels.managementCompanies.list("limit=500").then((res) => setMcList(res.items));
  }, []);

  useEffect(() => {
    reportsApi.managementDashboard(params).then(setManagementDashboard);
  }, [params]);

  const downloadExcel = async () => {
    try {
      await triggerDownload(reportsApi.exportUrl("management-dashboard", params));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "Download failed");
    }
  };

  if (!managementDashboard) {
    return (
      <div className="surface-panel p-6 text-sm text-slate-500">
        Loading management dashboard...
      </div>
    );
  }

  const atRiskCount = managementDashboard.summary.yellow_count + managementDashboard.summary.red_count;

  return (
    <div className="space-y-5 portal-page-shell">
      <PageHeader
        eyebrow="Portal dashboard"
        title="Management Dashboard"
        description={`${managementDashboard.summary.completed_calls} completed calls in ${periodLabel} with a median score of ${managementDashboard.summary.median_percent.toFixed(1)}%. Trend tables below cover ${trendRangeLabel}.`}
        serifTitle
        badges={[
          { label: `Snapshot ${periodLabel}`, tone: "neutral" },
          { label: managementDashboard.summary.management_company_name, tone: "brand" },
          { label: `${managementDashboard.summary.report_view_mode} view`, tone: "success" },
        ]}
      />

      <FilterToolbar sticky>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[7rem_7rem_minmax(16rem,1fr)_auto] xl:items-end">
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Year</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="select-shell w-full">
              {availableYears.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Month</span>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value === "ytd" ? "ytd" : Number(e.target.value))}
              className="select-shell w-full"
            >
              <option value="ytd">YTD</option>
              {MONTH_NAMES.map((label, index) => (
                <option key={label} value={index + 1}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Management Company</span>
            <select
              value={mcId ?? ""}
              onChange={(e) => setMcId(e.target.value ? Number(e.target.value) : undefined)}
              className="select-shell w-full"
              disabled={isGuest}
            >
              <option value="">All HMCs</option>
              {mcList.map((mc) => (
                <option key={mc.id} value={mc.id}>
                  {mc.name}
                </option>
              ))}
            </select>
          </label>

          <button onClick={downloadExcel} className="button-secondary h-11 !px-4 !py-2 text-sm">
            Export Excel
          </button>
        </div>
      </FilterToolbar>

      <MetricStrip
        items={[
          { label: "Completed calls", value: managementDashboard.summary.completed_calls, detail: `Completed in ${periodLabel}`, tone: "brand" },
          { label: "Median score", value: `${managementDashboard.summary.median_percent.toFixed(1)}%`, detail: `${periodLabel} snapshot`, tone: "success" },
          { label: "At-risk calls", value: atRiskCount, detail: "Yellow plus red performance rows", tone: "warning" },
          { label: "Score targets", value: `${managementDashboard.summary.report_view_mode} scoring`, detail: "Targets applied to this dashboard scope", tone: "accent" },
        ]}
      />

      <section className="grid gap-5 xl:grid-cols-3">
        <DataTableShell title="Median % Trend" subtitle={trendLabel}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Month</th>
                <th className="px-4 py-3 text-right">Median %</th>
              </tr>
            </thead>
            <tbody>
              {managementDashboard.percent_by_month.map((row) => (
                <tr key={row.month} className="border-t">
                  <td className="px-4 py-3">{row.month_name}</td>
                  <td className="px-4 py-3 text-right font-semibold">{row.median_percent.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>

        <DataTableShell title="Median Component Trend" subtitle={trendLabel} className="xl:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">Month</th>
                  <th className="px-3 py-3 text-right">Admin</th>
                  <th className="px-3 py-3 text-right">Hosp</th>
                  <th className="px-3 py-3 text-right">Qual</th>
                  <th className="px-3 py-3 text-right">Sell</th>
                  <th className="px-3 py-3 text-right">Close</th>
                  <th className="px-3 py-3 text-right">Prop/RFP</th>
                  <th className="px-3 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {managementDashboard.score_breakdown.map((row) => (
                  <tr key={row.month} className="border-t">
                    <td className="px-3 py-3">{row.month_name}</td>
                    <td className="px-3 py-3 text-right">{row.administration.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right">{row.hospitality.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right">{row.qualifying.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right">{row.selling.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right">{row.closing.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right">{row.prop_rfp.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right font-semibold">{row.total_score.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DataTableShell>
      </section>

      <section className="grid gap-5 xl:grid-cols-[0.7fr,1.3fr]">
        <DataTableShell title="Monthly Volume" subtitle={trendLabel}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Month</th>
                <th className="px-4 py-3 text-right">Calls</th>
              </tr>
            </thead>
            <tbody>
              {managementDashboard.volume_by_month.map((row) => (
                <tr key={row.month} className="border-t">
                  <td className="px-4 py-3">{row.month_name}</td>
                  <td className="px-4 py-3 text-right font-semibold">{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>

        <DataTableShell title={`Completed Calls in ${periodLabel}`} subtitle="Presentation-first detail rows for the selected period.">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">Shop ID</th>
                  <th className="px-3 py-3 text-left">Hotel</th>
                  <th className="px-3 py-3 text-left">Shopper</th>
                  <th className="px-3 py-3 text-left">Sales Staff</th>
                  <th className="px-3 py-3 text-left">Type</th>
                  <th className="px-3 py-3 text-left">Status</th>
                  <th className="px-3 py-3 text-right">%</th>
                  <th className="px-3 py-3 text-left">Completed</th>
                </tr>
              </thead>
              <tbody>
                {managementDashboard.details.length > 0 ? (
                  managementDashboard.details.map((row) => (
                    <tr key={row.call_id} className="border-t">
                      <td className="px-3 py-3 font-mono text-xs">{row.shop_id}</td>
                      <td className="px-3 py-3">
                        <div className="font-medium">{row.hotel_name}</div>
                        <div className="text-xs text-gray-500">{row.hotel_code}</div>
                      </td>
                      <td className="px-3 py-3">{row.shopper_name || "-"}</td>
                      <td className="px-3 py-3">{row.sales_staff || "-"}</td>
                      <td className="px-3 py-3">{row.call_type}</td>
                      <td className="px-3 py-3">{row.status}</td>
                      <td className="px-3 py-3 text-right font-semibold">{row.percent != null ? `${row.percent}%` : "-"}</td>
                      <td className="px-3 py-3">{row.completed_date || "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr className="border-t">
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                      No completed calls for {periodLabel}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DataTableShell>
      </section>

      <SectionCard title="Score targets" subtitle="Targets applied to the current portal view.">
        <div className="summary-list">
          <div className="summary-row">
            <span>Administration</span>
            <strong>{managementDashboard.summary.administration_target}</strong>
          </div>
          <div className="summary-row">
            <span>Hospitality</span>
            <strong>{managementDashboard.summary.hospitality_target}</strong>
          </div>
          <div className="summary-row">
            <span>Qualifying</span>
            <strong>{managementDashboard.summary.qualifying_target}</strong>
          </div>
          <div className="summary-row">
            <span>Selling</span>
            <strong>{managementDashboard.summary.selling_target}</strong>
          </div>
          <div className="summary-row">
            <span>Closing</span>
            <strong>{managementDashboard.summary.closing_target}</strong>
          </div>
          <div className="summary-row">
            <span>Prop / RFP</span>
            <strong>{managementDashboard.summary.prop_rfp_target}</strong>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
